import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  RefreshCw, Clock, CheckCircle, AlertCircle, Loader2, ArrowLeft,
  Database, Image, Newspaper, Users, MessageCircle, Sparkles,
  ShieldAlert, FlaskConical, Play, ChevronDown, ChevronUp, Circle, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Display-only gate. The real check is server-side (require_admin in
// backend/server.py, configured via the ADMIN_EMAILS env var) — keep in sync.
const ADMIN_EMAILS = ["ramster@gmail.com"];
const MAX_HISTORY = 5;

// Admin endpoints are now auth-gated server-side, so every call from this
// page must carry the session cookie. Global default is safe: same-origin
// requests ignore it, and public endpoints don't require credentials.
axios.defaults.withCredentials = true;

// ── Health checks definition ──────────────────────────────────────────────────
// Each check is a pure async function that returns { pass, value, detail }
// They call existing API endpoints — no new backend code required.

const CHECKS = [
  {
    id: "db",
    label: "Database",
    group: "Infrastructure",
    description: "MongoDB reachable and responding",
    run: async () => {
      const res = await axios.get(`${API}/health`, { timeout: 8000 });
      const ok = res.data?.db === "ok";
      return { pass: ok, value: ok ? "Connected" : "Error", detail: ok ? null : "DB ping failed" };
    }
  },
  {
    id: "scheduler",
    label: "Background Scheduler",
    group: "Infrastructure",
    description: "Content refresh loop is running",
    run: async () => {
      const res = await axios.get(`${API}/health`, { timeout: 8000 });
      const ok = res.data?.scheduler_running === true;
      return { pass: ok, value: ok ? "Running" : "Stopped", detail: ok ? null : "Scheduler not active — content won't refresh" };
    }
  },
  {
    id: "fal_key",
    label: "FAL_KEY Format",
    group: "Infrastructure",
    description: "fal.ai API key is set and correctly formatted",
    run: async () => {
      const res = await axios.get(`${API}/scheduler/status`, { timeout: 8000 });
      const img = res.data?.image_generation || {};
      const keySet = img.fal_key_set;
      const keyValid = img.fal_key_format_valid;
      if (!keySet) return { pass: false, value: "Missing", detail: "FAL_KEY env var not set" };
      if (!keyValid) return { pass: false, value: "Bad format", detail: "FAL_KEY must be 'key-id:secret' format" };
      return { pass: true, value: "Valid", detail: null };
    }
  },
  {
    id: "circuit_breaker",
    label: "Circuit Breaker",
    group: "Infrastructure",
    description: "fal.ai circuit breaker is closed (not tripped)",
    run: async () => {
      const res = await axios.get(`${API}/health`, { timeout: 8000 });
      const open = res.data?.fal_circuit_open;
      return {
        pass: !open,
        value: open ? "OPEN — image gen paused" : "Closed",
        detail: open ? "Too many fal.ai failures. Use 'Reset Circuit Breaker' button." : null
      };
    }
  },
  {
    id: "content_freshness",
    label: "Content Freshness",
    group: "Content",
    description: "New articles were actually published in the last 24h. This is the throughput alarm — every other check tests a component (fal, art director, classifier) and can be green while the pipeline publishes nothing.",
    run: async () => {
      const res = await axios.get(`${API}/health`, { timeout: 8000 });
      const d = res.data || {};
      const n = d.articles_ingested_24h ?? 0;
      const hrs = d.hours_since_last_article;
      const ok = n > 0;
      return {
        pass: ok,
        value: ok ? `${n} new in 24h` : `NOTHING in 24h`,
        detail: ok
          ? null
          : `Newest article is ${hrs ?? "?"}h old. Ingestion is blocked — check backend logs for "Skipping" lines (dedup guards, safety rejections) or scheduler errors.`,
      };
    }
  },
  {
    id: "article_count",
    label: "Article Count",
    group: "Content",
    description: "At least 10 articles exist across all categories",
    run: async () => {
      const res = await axios.get(`${API}/meta`, { timeout: 8000 });
      const count = res.data?.total_articles || 0;
      const ok = count >= 10;
      return { pass: ok, value: `${count} articles`, detail: ok ? null : `Only ${count} articles — run Force Refresh` };
    }
  },
  {
    id: "categories",
    label: "All Categories Populated",
    group: "Content",
    description: "world, science, tech, sports, people each have ≥1 article",
    run: async () => {
      const res = await axios.get(`${API}/articles`, { timeout: 10000 });
      const articles = res.data || [];
      const required = ["world", "science", "tech", "sports", "people"];
      const found = new Set(articles.map(a => a.category));
      const missing = required.filter(c => !found.has(c));
      const ok = missing.length === 0;
      return {
        pass: ok,
        value: ok ? "All present" : `Missing: ${missing.join(", ")}`,
        detail: ok ? null : `Categories with no articles: ${missing.join(", ")}`
      };
    }
  },
  {
    id: "image_coverage_recent",
    label: "Recent Image Coverage (24h)",
    group: "Content",
    description: "≥75% of articles ingested in the last 24h have AI-generated images. Catches a broken ingestion pipeline; the lifetime coverage stat below stays high from historical data and would otherwise hide this.",
    run: async () => {
      const res = await axios.get(`${API}/images/status?recent_hours=24`, { timeout: 8000 });
      const d = res.data || {};
      const pct = d.recent_coverage_pct || 0;
      // If nothing was ingested in the last 24h we can't make a judgment.
      if ((d.recent_total || 0) === 0) {
        return { pass: null, value: "No recent articles", detail: "Nothing ingested in last 24h — can't judge" };
      }
      const ok = pct >= 75;
      return {
        pass: ok,
        value: `${pct}% (${d.recent_total - d.recent_missing}/${d.recent_total})`,
        detail: ok ? null : `${d.recent_missing} of the last 24h's articles missing images — image generation likely broken right now`,
      };
    }
  },
  {
    id: "fal_probe",
    label: "fal.ai Live Probe",
    group: "Infrastructure",
    description: "Actually asks fal.ai to generate a tiny test image end-to-end. Catches outages the static checks (key format, circuit state) miss.",
    run: async () => {
      const res = await axios.post(`${API}/images/probe`, {}, { timeout: 60000 });
      const ok = res.data?.ok;
      return {
        pass: ok,
        value: ok ? "OK" : `Failed at ${res.data?.stage || "?"}`,
        detail: ok ? null : (res.data?.error || "Unknown failure"),
      };
    }
  },
  {
    id: "artdir_probe",
    label: "AI Art Director Live Probe",
    group: "Infrastructure",
    description: "Asks the art director to write a scene for a fixed test article (a dolphin story). Fails if it errors out or falls back to generic pool scenes — the cause of irrelevant/duplicate images. One cheap Gemini call, no image cost.",
    run: async () => {
      const res = await axios.post(`${API}/images/artdir-probe`, {}, { timeout: 60000 });
      const d = res.data || {};
      const stats = d.stats_since_restart || {};
      if (d.ok) {
        return { pass: true, value: `"${(d.scene || "").slice(0, 60)}..."`, detail: null };
      }
      return {
        pass: false,
        value: d.used_fallback ? "FELL BACK to pool scene" : "Generic scene",
        detail: `model=${d.model} · since restart: ${stats.success || 0} ok / ${stats.fallbacks || 0} fallbacks · last error: ${stats.last_error || "none recorded"}`,
      };
    }
  },
  {
    id: "image_coverage",
    label: "Lifetime Image Coverage ≥75%",
    group: "Content",
    description: "≥75% of ALL articles have images. Historical metric — stays green even when current ingestion is failing, so don't rely on this alone.",
    run: async () => {
      const res = await axios.get(`${API}/images/status`, { timeout: 8000 });
      const pct = res.data?.coverage_pct || 0;
      const ok = pct >= 75;
      return {
        pass: ok,
        value: `${pct}%`,
        detail: ok ? null : `${res.data?.missing_images} articles missing images — use 'Retry Images'`
      };
    }
  },
  {
    id: "topic_diversity",
    label: "Topic Diversity",
    group: "Content",
    description: "No single topic cluster exceeds 2 articles",
    run: async () => {
      const [res, patternsRes] = await Promise.all([
        axios.get(`${API}/articles`, { timeout: 10000 }),
        // Use the backend's canonical topic patterns — no local copy to drift.
        axios.get(`${API}/config/topic-patterns`, { timeout: 8000 }),
      ]);
      const articles = res.data || [];
      const TOPIC_PATTERNS = (patternsRes.data?.patterns || []).map(p => [p.topic, p.keywords]);
      const counts = {};
      for (const a of articles) {
        const text = `${a.original_content || ""} ${a.headline || ""} ${a.deck || ""}`.toLowerCase();
        for (const [topic, kws] of TOPIC_PATTERNS) {
          if (kws.some(kw => text.includes(kw))) {
            counts[topic] = (counts[topic] || 0) + 1;
            break;
          }
        }
      }
      const violations = Object.entries(counts).filter(([, c]) => c > 2);
      const ok = violations.length === 0;
      return {
        pass: ok,
        value: ok ? "Diverse" : `${violations.length} topic(s) over limit`,
        detail: ok ? null : violations.map(([t, c]) => `"${t}": ${c} articles`).join(", ")
      };
    }
  },
  {
    id: "poll",
    label: "Active Poll",
    group: "Content",
    description: "A poll with ≥2 options is currently active",
    run: async () => {
      const res = await axios.get(`${API}/poll`, { timeout: 8000 });
      const poll = res.data;
      const ok = poll?.question && (poll?.options?.length || 0) >= 2;
      return { pass: ok, value: ok ? `"${poll.question.slice(0, 40)}..."` : "Missing", detail: ok ? null : "No active poll found" };
    }
  },
  {
    id: "wonders",
    label: "Quick Wonders",
    group: "Content",
    description: "At least 3 Quick Wonders facts are available",
    run: async () => {
      const res = await axios.get(`${API}/wonders`, { timeout: 8000 });
      const count = (res.data || []).length;
      const ok = count >= 3;
      return { pass: ok, value: `${count} wonders`, detail: ok ? null : `Only ${count} wonders — run Daily Refresh` };
    }
  },
  {
    id: "hero_stories",
    label: "Hero Stories",
    group: "Content",
    description: "At least 1 People Making a Difference story exists",
    run: async () => {
      const res = await axios.get(`${API}/people`, { timeout: 8000 });
      const count = (res.data || []).length;
      const ok = count >= 1;
      return { pass: ok, value: `${count} stories`, detail: ok ? null : "No hero stories found" };
    }
  },
  {
    id: "moderation_gate",
    label: "Safety Gate",
    group: "Content",
    description: "Kid-safety classifier is recording decisions (fail-closed, auditable)",
    run: async () => {
      const res = await axios.get(`${API}/moderation/stats?hours=168`, { timeout: 8000 });
      const d = res.data || {};
      const c = d.counts || {};
      const infra = c.blocked_infra || 0;
      // Healthy = we have a decision trail and the classifier isn't failing infra-wide.
      const ok = (d.total_decisions || 0) > 0 && infra === 0;
      const value = `${c.passed || 0} ok / ${c.blocked_content || 0} blocked`;
      let detail = null;
      if ((d.total_decisions || 0) === 0) {
        detail = "No moderation decisions logged yet — run a content refresh";
      } else if (infra > 0) {
        detail = `${infra} article(s) held due to classifier errors — check MODEL_SAFETY / gateway`;
      }
      return { pass: ok, value, detail };
    }
  },
  {
    id: "safety_regression",
    label: "Safety Classifier Regression",
    group: "Quality",
    description: "Runs a fixed corpus of should-pass and should-block stories through the classifier. Catches prompt regressions when the rubric is tightened or loosened. Costs ~12 LLM calls per run (~$0.01).",
    run: async () => {
      const res = await axios.post(`${API}/moderation/regression-test`, {}, { timeout: 180000 });
      const d = res.data || {};
      const ok = d.failed === 0;
      const failedFixtures = (d.results || []).filter(r => !r.passed);
      const detail = ok
        ? null
        : failedFixtures.map(f =>
            `${f.name}: expected ${f.expected_safe ? "PASS" : "BLOCK"}, got ${f.actual_safe ? "PASS" : "BLOCK"}${f.reason ? ` (${f.reason})` : ""}`
          ).join("\n");
      return { pass: ok, value: `${d.passed}/${d.total} fixtures`, detail };
    }
  },
  {
    id: "no_em_dashes",
    label: "No Em Dashes in Content",
    group: "Quality",
    description: "Headlines and decks should not contain em dashes (—)",
    run: async () => {
      const res = await axios.get(`${API}/articles`, { timeout: 10000 });
      const articles = res.data || [];
      const violations = articles.filter(a =>
        (a.headline || "").includes("—") || (a.deck || "").includes("—")
      );
      const ok = violations.length === 0;
      return {
        pass: ok,
        value: ok ? "Clean" : `${violations.length} violation(s)`,
        detail: ok ? null : `Articles with em dashes: ${violations.slice(0, 2).map(a => `"${(a.headline||"").slice(0,40)}"`).join(", ")}`
      };
    }
  },
  {
    id: "lead_article",
    label: "Lead Article",
    group: "Quality",
    description: "A lead/featured article exists with a headline",
    run: async () => {
      const res = await axios.get(`${API}/articles/lead`, { timeout: 8000 });
      const ok = !!res.data?.headline;
      return { pass: ok, value: ok ? `"${res.data.headline.slice(0, 40)}..."` : "Missing", detail: ok ? null : "No lead article found" };
    }
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusColor(pass) {
  if (pass === true) return "text-emerald-600";
  if (pass === false) return "text-red-500";
  return "text-gray-400";
}

function statusBg(pass) {
  if (pass === true) return "bg-emerald-50 border-emerald-100";
  if (pass === false) return "bg-red-50 border-red-200";
  return "bg-gray-50 border-gray-200";
}

function StatusIcon({ pass, running }) {
  if (running) return <Loader2 className="w-4 h-4 animate-spin text-blue-400 shrink-0" />;
  if (pass === true) return <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (pass === false) return <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />;
  return <Circle className="w-4 h-4 text-gray-300 shrink-0" />;
}

function OverallScore({ results }) {
  const ran = results.filter(r => r.pass !== null);
  if (ran.length === 0) return null;
  const passed = ran.filter(r => r.pass === true).length;
  const pct = Math.round((passed / ran.length) * 100);
  const color = pct === 100 ? "text-emerald-600" : pct >= 70 ? "text-amber-500" : "text-red-500";
  const bg = pct === 100 ? "bg-emerald-50 border-emerald-200" : pct >= 70 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
  return (
    <div className={`rounded-xl border px-5 py-4 flex items-center justify-between ${bg}`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-0.5">Overall</p>
        <p className={`text-2xl font-black ${color}`}>{passed}/{ran.length} checks passed</p>
      </div>
      <div className={`text-4xl font-black ${color}`}>{pct}%</div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();

  // Existing state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [imageStatus, setImageStatus] = useState(null);
  const [meta, setMeta] = useState(null);
  const [peopleCount, setPeopleCount] = useState(0);
  const [wondersInfo, setWondersInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forceRefreshing, setForceRefreshing] = useState(false);
  const [dailyRefreshing, setDailyRefreshing] = useState(false);
  const [retryingImages, setRetryingImages] = useState(false);

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersFetched, setSubscribersFetched] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [sweepReport, setSweepReport] = useState(null);  // {flagged_count, flagged_articles, deleted_count}
  const [sweeping, setSweeping] = useState(false);
  const [traceResult, setTraceResult] = useState(null);  // {ok, steps: [{stage, ok, ms, detail}]}
  const [tracing, setTracing] = useState(false);
  const [readerReports, setReaderReports] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState("dashboard");

  // System checks state
  const initialResults = CHECKS.map(c => ({ id: c.id, pass: null, value: null, detail: null, running: false }));
  const [checkResults, setCheckResults] = useState(initialResults);
  const [checksRunning, setChecksRunning] = useState(false);
  const [runHistory, setRunHistory] = useState([]); // [{timestamp, results, score}]
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const abortRef = useRef(false);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const [statusRes, metaRes, peopleRes, imageRes, wondersRes] = await Promise.all([
        axios.get(`${API}/scheduler/status`),
        axios.get(`${API}/meta`),
        axios.get(`${API}/people/count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/images/status`).catch(() => ({ data: null })),
        axios.get(`${API}/wonders/count`).catch(() => ({ data: { count: 0, last_updated: null } }))
      ]);
      setSchedulerStatus(statusRes.data);
      setMeta(metaRes.data);
      setPeopleCount(peopleRes.data.count);
      setImageStatus(imageRes.data);
      try {
        const reportsRes = await axios.get(`${API}/feedback/reports?status=new`);
        setReaderReports(reportsRes.data?.reports || []);
      } catch { /* reports are non-critical for the dashboard */ }
      setWondersInfo(wondersRes.data);
    } catch (error) {
      console.error("Error fetching status:", error);
      toast.error("Failed to fetch scheduler status");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const res = await axios.get(`${API}/digest/subscribers/list`);
      setSubscribers(res.data || []);
      setSubscribersFetched(true);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setSubscribersLoading(false);
    }
  };

  const runModerationSweep = async (action, useClassifier = false) => {
    if (action === "delete") {
      const n = sweepReport?.flagged_count ?? 0;
      if (!window.confirm(`Permanently delete ${n} article(s) flagged by the safety scan? This cannot be undone.`)) return;
      // Delete must re-run the same scan mode that produced the report the
      // admin just reviewed, or it would delete a different set.
      useClassifier = !!sweepReport?.classifier_scanned;
    }
    setSweeping(true);
    try {
      const params = `action=${action}${useClassifier ? "&use_classifier=true" : ""}`;
      // Deep scan runs one LLM call per article — allow a few minutes.
      const res = await axios.post(`${API}/moderation/sweep?${params}`, {}, { timeout: useClassifier ? 600000 : 30000 });
      setSweepReport(res.data);
      if (action === "delete") {
        toast.success(`Deleted ${res.data?.deleted_count || 0} article(s)`);
      } else {
        toast.success(`Scan complete: ${res.data?.flagged_count || 0} flagged of ${res.data?.scanned || 0}${useClassifier ? ` (AI re-checked ${res.data?.classifier_scanned || 0})` : ""}`);
      }
    } catch {
      toast.error("Moderation sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const sendTestDigest = async () => {
    const email = testEmail.trim();
    if (!email) { toast.error("Enter an email address"); return; }
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/digest/test?email=${encodeURIComponent(email)}`);
      toast.success(`Test digest sent to ${email} (${res.data?.stories || 0} stories)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestSending(false);
    }
  };

  // ── Run all checks sequentially ──
  const runChecks = async () => {
    if (checksRunning) return;
    setChecksRunning(true);
    abortRef.current = false;
    setCheckResults(CHECKS.map(c => ({ id: c.id, pass: null, value: null, detail: null, running: false })));

    const finalResults = [];

    for (let i = 0; i < CHECKS.length; i++) {
      if (abortRef.current) break;
      const check = CHECKS[i];

      // Mark this check as running
      setCheckResults(prev => prev.map(r => r.id === check.id ? { ...r, running: true } : r));

      let result;
      try {
        result = await check.run();
      } catch (err) {
        result = { pass: false, value: "Error", detail: err?.message || "Check threw an exception" };
      }

      const enriched = { id: check.id, ...result, running: false };
      finalResults.push(enriched);

      setCheckResults(prev => prev.map(r => r.id === check.id ? enriched : r));

      // Small gap between checks for readability
      await new Promise(res => setTimeout(res, 120));
    }

    const passed = finalResults.filter(r => r.pass).length;
    const score = Math.round((passed / finalResults.length) * 100);
    const runRecord = {
      timestamp: new Date().toISOString(),
      score,
      passed,
      total: finalResults.length,
      results: finalResults
    };

    setLastRun(runRecord.timestamp);
    setRunHistory(prev => [runRecord, ...prev].slice(0, MAX_HISTORY));
    setChecksRunning(false);

    if (score === 100) toast.success("All checks passed!");
    else if (score >= 70) toast.warning(`${passed}/${finalResults.length} checks passed`);
    else toast.error(`Only ${passed}/${finalResults.length} checks passed — review failures`);
  };

  // ── Existing handlers ──
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/refresh-content`);
      toast.success(response.data.message);
      await fetchStatus();
    } catch { toast.error("Failed to refresh content"); }
    finally { setRefreshing(false); }
  };

  const handleForceRefresh = async () => {
    setForceRefreshing(true);
    toast.info("Force refresh started - this may take a minute...");
    try {
      const response = await axios.post(`${API}/refresh-content/force`, {}, { timeout: 120000 });
      toast.success(response.data.message);
      await fetchStatus();
    } catch (error) {
      if (error.code === "ECONNABORTED") toast.info("Refresh is running in the background.");
      else toast.error("Failed to force refresh content");
    } finally { setForceRefreshing(false); }
  };

  const handleDailyRefresh = async () => {
    setDailyRefreshing(true);
    toast.info("Generating new poll and hero story...");
    try {
      const response = await axios.post(`${API}/refresh-daily`, {}, { timeout: 60000 });
      if (response.data.success) toast.success(response.data.message);
      else toast.warning(response.data.message);
      await fetchStatus();
    } catch { toast.error("Failed to generate daily content"); }
    finally { setDailyRefreshing(false); }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short"
    });
  };

  const formatTimeUntil = (timeStr) => {
    if (!timeStr || timeStr === "N/A") return "N/A";
    const parts = timeStr.split(":");
    if (parts.length >= 2) return `${parseInt(parts[0])}h ${parseInt(parts[1])}m`;
    return timeStr;
  };

  // ── Auth guards ──
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-scoop-blue mx-auto mb-2" />
          <p className="text-gray-500">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!user || !ADMIN_EMAILS.includes(user.email)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 pb-6 text-center">
            <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-gray-900 mb-2">Admin Access Required</h2>
            <p className="text-sm text-gray-500 mb-6">
              {user ? `${user.email} is not authorized.` : "Please sign in with an authorized account."}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              {!user && <Button onClick={login} className="bg-scoop-blue hover:bg-scoop-blue/90">Sign In</Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Group checks by group label ──
  const groups = [...new Set(CHECKS.map(c => c.group))];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-display font-bold text-xl text-scoop-ink">
                The <em className="text-scoop-blue">Scoop</em> Admin
              </h1>
              <p className="text-xs text-gray-500">Content Management & Scheduler</p>
            </div>
          </div>
          <Badge variant={schedulerStatus?.scheduler_running ? "default" : "destructive"}>
            {schedulerStatus?.scheduler_running ? "Scheduler Active" : "Scheduler Stopped"}
          </Badge>
        </div>

        {/* Tab bar */}
        <div className="max-w-4xl mx-auto px-6 flex gap-1 border-t border-gray-100">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Database className="w-3.5 h-3.5" /> },
            { id: "checks", label: "System Checks", icon: <FlaskConical className="w-3.5 h-3.5" /> },
            { id: "subscribers", label: "Subscribers", icon: <Mail className="w-3.5 h-3.5" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "subscribers" && !subscribersFetched) fetchSubscribers();
              }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? "border-scoop-blue text-scoop-blue"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* ── DASHBOARD TAB ── */}
        {activeTab === "dashboard" && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Newspaper className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{meta?.total_articles || 0}</p><p className="text-xs text-gray-500">Articles</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg"><Users className="w-5 h-5 text-amber-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{peopleCount || 4}</p><p className="text-xs text-gray-500">Hero Stories</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg"><Database className="w-5 h-5 text-green-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{meta?.categories?.length || 0}</p><p className="text-xs text-gray-500">Categories</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg"><Clock className="w-5 h-5 text-purple-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{schedulerStatus?.cache_duration_hours || 6}h</p><p className="text-xs text-gray-500">News Refresh</p></div>
                </div>
              </CardContent></Card>
            </div>

            {/* Scheduler Status */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Scheduler Status</CardTitle>
                    <CardDescription>Automatic content refresh runs every {schedulerStatus?.cache_duration_hours || 6} hours</CardDescription>
                  </div>
                  {schedulerStatus?.scheduler_running
                    ? <CheckCircle className="w-6 h-6 text-green-500" />
                    : <AlertCircle className="w-6 h-6 text-red-500" />}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Last Refresh</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDateTime(schedulerStatus?.last_refresh)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Next Scheduled Refresh</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDateTime(schedulerStatus?.next_scheduled_refresh)}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Time Until Next Refresh</p>
                        <p className="text-xl font-bold text-blue-700">{formatTimeUntil(schedulerStatus?.time_until_next_refresh)}</p>
                      </div>
                      <RefreshCw className={`w-8 h-8 text-blue-400 ${schedulerStatus?.scheduler_running ? "animate-spin-slow" : ""}`} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Manual Refresh */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Manual Content Refresh</CardTitle>
                <CardDescription>Manually trigger a content refresh to fetch new articles from RSS feeds</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800"><strong>Note:</strong> Each refresh uses AI credits. Use sparingly.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={handleRefresh} disabled={refreshing || forceRefreshing} variant="outline" className="flex-1" data-testid="refresh-btn">
                      {refreshing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</> : <><RefreshCw className="w-4 h-4 mr-2" />Smart Refresh</>}
                    </Button>
                    <Button onClick={handleForceRefresh} disabled={refreshing || forceRefreshing} className="flex-1 bg-scoop-blue hover:bg-scoop-blue/90" data-testid="force-refresh-btn">
                      {forceRefreshing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Refreshing...</> : <><RefreshCw className="w-4 h-4 mr-2" />Force Refresh Now</>}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-700 mb-1">Smart Refresh</p>
                      <p className="text-gray-500 text-xs">Only refreshes if 6+ hours since last refresh.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-700 mb-1">Force Refresh</p>
                      <p className="text-gray-500 text-xs">Ignores cache, fetches new content immediately.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Image Health */}
            <Card className={imageStatus?.fal_circuit_open ? "border-red-300 bg-red-50/30" : imageStatus?.missing_images > 0 ? "border-amber-300 bg-amber-50/30" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><Image className="w-5 h-5" /> Image Generation Health</CardTitle>
                    <CardDescription>AI Art Director (Gemini) + fal.ai flux/dev</CardDescription>
                  </div>
                  {imageStatus?.fal_circuit_open
                    ? <Badge variant="destructive">Circuit Open</Badge>
                    : imageStatus?.missing_images > 0
                      ? <Badge className="bg-amber-500">{imageStatus.missing_images} Missing</Badge>
                      : <Badge className="bg-green-500">All Good</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-white rounded-lg border text-center">
                      <p className="text-2xl font-bold text-gray-900">{imageStatus?.total_articles || 0}</p>
                      <p className="text-xs text-gray-500">Total Articles</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border text-center">
                      <p className="text-2xl font-bold text-green-600">{imageStatus?.with_images || 0}</p>
                      <p className="text-xs text-gray-500">With Images</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border text-center">
                      <p className={`text-2xl font-bold ${imageStatus?.missing_images > 0 ? "text-red-600" : "text-gray-400"}`}>{imageStatus?.missing_images || 0}</p>
                      <p className="text-xs text-gray-500">Missing</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Image Coverage</span><span>{imageStatus?.coverage_pct || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all ${imageStatus?.coverage_pct >= 90 ? "bg-green-500" : imageStatus?.coverage_pct >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                        style={{ width: `${imageStatus?.coverage_pct || 0}%` }} />
                    </div>
                  </div>
                  {imageStatus?.fal_last_error && (
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Last Error</p>
                      <p className="text-sm text-red-800 font-mono">{imageStatus.fal_last_error}</p>
                    </div>
                  )}
                  {imageStatus?.missing_images > 0 && (
                    <div className={`p-3 rounded-lg border ${imageStatus.stuck_at_cap > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Missing breakdown</p>
                      <p className="text-sm text-gray-800">
                        {Math.max(0, (imageStatus.missing_images || 0) - (imageStatus.stuck_at_cap || 0) - (imageStatus.in_progress_attempts || 0))} waiting ·{" "}
                        {imageStatus.in_progress_attempts || 0} retrying ·{" "}
                        <span className={imageStatus.stuck_at_cap > 0 ? "text-amber-700 font-semibold" : ""}>
                          {imageStatus.stuck_at_cap || 0} gave up (attempt cap)
                        </span>{" "}
                        · {imageStatus.recent_generations || 0} generated in 24h
                      </p>
                      {imageStatus.stuck_at_cap > 0 && (
                        <p className="text-xs text-amber-700 mt-1">
                          "Gave up" articles are skipped by backfill until you click Reset Circuit Breaker &amp; Retry Images (clears the attempt counters).
                        </p>
                      )}
                    </div>
                  )}
                  {imageStatus?.artdir_stats && (
                    <div className={`p-3 rounded-lg border ${imageStatus.artdir_stats.fallbacks > (imageStatus.artdir_stats.success || 0) ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        AI Art Director (since restart) · {imageStatus.artdir_model}
                      </p>
                      <p className="text-sm text-gray-800">
                        {imageStatus.artdir_stats.success || 0} scenes written ·{" "}
                        {imageStatus.artdir_stats.generic_retries || 0} retries ·{" "}
                        <span className={imageStatus.artdir_stats.fallbacks > 0 ? "text-red-600 font-semibold" : ""}>
                          {imageStatus.artdir_stats.fallbacks || 0} fallbacks
                        </span>
                      </p>
                      {imageStatus.artdir_stats.last_error && (
                        <p className="text-xs text-red-700 font-mono mt-1 break-all">{imageStatus.artdir_stats.last_error}</p>
                      )}
                    </div>
                  )}
                  {schedulerStatus?.image_generation && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">FAL_KEY Set</p>
                        <p className={`font-semibold ${schedulerStatus.image_generation.fal_key_set ? "text-green-600" : "text-red-600"}`}>
                          {schedulerStatus.image_generation.fal_key_set ? "Yes" : "No"}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Key Format Valid</p>
                        <p className={`font-semibold ${schedulerStatus.image_generation.fal_key_format_valid ? "text-green-600" : "text-red-600"}`}>
                          {schedulerStatus.image_generation.fal_key_format_valid ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                  )}
                  <Button onClick={async () => {
                    setRetryingImages(true);
                    try {
                      const res = await axios.post(`${API}/images/retry`, {}, { timeout: 120000 });
                      toast.success(res.data.message);
                      await fetchStatus();
                    } catch { toast.error("Image retry failed"); }
                    finally { setRetryingImages(false); }
                  }} disabled={retryingImages} variant="outline" className="w-full" data-testid="retry-images-btn">
                    {retryingImages ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Retrying...</> : <><Image className="w-4 h-4 mr-2" />Reset Circuit Breaker & Retry Images</>}
                  </Button>
                  <Button onClick={async () => {
                    setRetryingImages(true);
                    try {
                      const scan = await axios.post(`${API}/images/dedupe?action=report`, {}, { timeout: 30000 });
                      const dupGroups = scan.data?.duplicate_groups || 0;
                      const generic = scan.data?.generic_count || 0;
                      const total = scan.data?.to_regenerate_count || 0;
                      if (total === 0) {
                        toast.success("No bad images found (no duplicates or generic templates)");
                        return;
                      }
                      const msg =
                        `Found ${dupGroups} duplicate group(s) and ${generic} article(s) with generic-template scenes ` +
                        `(${total} articles total to regenerate). Clear them and let backfill regenerate? Originals of duplicate groups are kept.`;
                      if (!window.confirm(msg)) return;
                      const res = await axios.post(`${API}/images/dedupe?action=clear`, {}, { timeout: 60000 });
                      toast.success(`Cleared ${res.data?.cleared_count || 0} images — backfill scheduled`);
                      await fetchStatus();
                    } catch { toast.error("Image dedupe failed"); }
                    finally { setRetryingImages(false); }
                  }} disabled={retryingImages} variant="outline" className="w-full" data-testid="dedupe-images-btn">
                    {retryingImages ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working...</> : <><Image className="w-4 h-4 mr-2" />Find & Regenerate Bad Images</>}
                  </Button>
                  <Button onClick={async () => {
                    setTracing(true);
                    setTraceResult(null);
                    try {
                      const res = await axios.post(`${API}/images/trace`, {}, { timeout: 120000 });
                      setTraceResult(res.data);
                      if (res.data?.ok) toast.success("Pipeline trace succeeded — one image generated and saved");
                      else toast.error("Pipeline trace failed — see the step breakdown below");
                    } catch { toast.error("Trace request failed"); }
                    finally { setTracing(false); }
                  }} disabled={tracing} className="w-full bg-scoop-blue hover:bg-scoop-blue/90 text-white" data-testid="trace-images-btn">
                    {tracing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Tracing (up to 60s)...</> : <>🔍 Diagnose Image Pipeline (runs 1 real image)</>}
                  </Button>
                  {traceResult?.steps && (
                    <div className={`p-3 rounded-lg border text-xs space-y-1.5 ${traceResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                      {traceResult.steps.map((s, i) => (
                        <div key={i} className="flex gap-2">
                          <span>{s.ok ? "✅" : "❌"}</span>
                          <span className="font-semibold shrink-0">{s.stage}</span>
                          {s.ms != null && <span className="text-gray-400 shrink-0">{s.ms}ms</span>}
                          <span className="text-gray-700 break-all">{s.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Content Moderation — retroactive sweep */}
            <Card className={sweepReport?.flagged_count > 0 ? "border-red-300 bg-red-50/30" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Content Moderation Sweep</CardTitle>
                <CardDescription>Re-check existing articles against the current safety rules. Quick Scan = keyword blocklist (free). Deep Scan = also re-runs the AI classifier on the newest 200 — the only way to catch rubric-only violations like political or sectarian framing (~$0.20).</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button onClick={() => runModerationSweep("report")} disabled={sweeping} variant="outline" className="flex-1" data-testid="sweep-report-btn">
                      {sweeping ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</> : <>Quick Scan</>}
                    </Button>
                    <Button onClick={() => runModerationSweep("report", true)} disabled={sweeping} variant="outline" className="flex-1" data-testid="sweep-deep-btn">
                      {sweeping ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</> : <>Deep Scan (AI)</>}
                    </Button>
                    <Button
                      onClick={() => runModerationSweep("delete")}
                      disabled={sweeping || !sweepReport?.flagged_count}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="sweep-delete-btn"
                    >
                      Delete Flagged ({sweepReport?.flagged_count || 0})
                    </Button>
                  </div>
                  {sweepReport && (
                    <div className="text-sm text-gray-700">
                      <p className="mb-2">
                        Scanned <strong>{sweepReport.scanned}</strong> articles
                        {sweepReport.classifier_scanned > 0 && <> (AI re-checked <strong>{sweepReport.classifier_scanned}</strong>)</>} ·
                        Flagged <strong>{sweepReport.flagged_count}</strong>
                        {sweepReport.deleted_count > 0 && <> · Deleted <strong>{sweepReport.deleted_count}</strong></>}
                      </p>
                      {sweepReport.flagged_articles?.length > 0 && (
                        <ul className="space-y-1 max-h-48 overflow-y-auto text-xs bg-white p-3 rounded border border-gray-200">
                          {sweepReport.flagged_articles.map((a, i) => (
                            <li key={a.id || i} className="text-gray-700">
                              <span className="text-gray-400 mr-1">[{a.category}]</span>{a.headline}
                              {a.flagged_by === "classifier" && <span className="ml-1 text-purple-500">AI: {a.reason || (a.concerns || []).join(", ")}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Reader reports — the "tell us" promise from the guidelines */}
            <Card className={readerReports.length > 0 ? "border-red-300 bg-red-50/30" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Reader Reports
                  {readerReports.length > 0 && <Badge className="bg-red-500">{readerReports.length} new</Badge>}
                </CardTitle>
                <CardDescription>Anonymous "something not right" reports from story pages. When one is valid, fix the rule (rubric/fixtures), not just the story.</CardDescription>
              </CardHeader>
              <CardContent>
                {readerReports.length === 0 ? (
                  <p className="text-sm text-gray-500">No new reports 🎉</p>
                ) : (
                  <ul className="space-y-3 max-h-64 overflow-y-auto">
                    {readerReports.map((r) => (
                      <li key={r.id} className="text-sm bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{r.headline || "(no article linked)"}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              <span className="font-bold uppercase">{r.reason}</span>
                              {r.comment && <> — “{r.comment}”</>}
                            </p>
                            {r.article_id && (
                              <a href={`/story/${r.article_id}`} target="_blank" rel="noreferrer" className="text-xs text-scoop-blue hover:underline">
                                View story →
                              </a>
                            )}
                          </div>
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              await axios.post(`${API}/feedback/reports/${r.id}/resolve`);
                              setReaderReports((prev) => prev.filter((x) => x.id !== r.id));
                            } catch { toast.error("Couldn't resolve report"); }
                          }}>
                            Mark reviewed
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Daily Content */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-600" /> Daily Content</CardTitle>
                <CardDescription>Poll + Hero Story + Quick Wonders — generated every 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <div className="flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-amber-600" /><p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Daily Poll</p></div>
                      <p className="text-sm text-gray-700">AI-generated kid-friendly poll question</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-amber-600" /><p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hero Stories</p></div>
                      <p className="text-sm text-gray-700">{peopleCount || 0} total · Latest 3 on homepage</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-amber-600" /><p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quick Wonders</p></div>
                      <p className="text-sm text-gray-700">{wondersInfo?.count || 0} wonders · {wondersInfo?.last_updated ? `Updated ${new Date(wondersInfo.last_updated).toLocaleDateString()}` : "Pending"}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-amber-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Last Daily Refresh</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDateTime(schedulerStatus?.last_daily_refresh)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Next Daily Refresh</p>
                        <p className="text-sm font-semibold text-gray-900">{schedulerStatus?.next_daily_refresh ? formatDateTime(schedulerStatus.next_daily_refresh) : "Pending"}</p>
                      </div>
                    </div>
                  </div>
                  <Button onClick={handleDailyRefresh} disabled={dailyRefreshing || refreshing || forceRefreshing} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="daily-refresh-btn">
                    {dailyRefreshing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate New Daily Content Now</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ── SYSTEM CHECKS TAB ── */}
        {activeTab === "checks" && (
          <div className="space-y-5">

            {/* Run button + score */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">System Checks</h2>
                <p className="text-sm text-gray-500">
                  {lastRun ? `Last run: ${formatDateTime(lastRun)}` : "Run checks to validate platform health"}
                </p>
              </div>
              <Button
                onClick={runChecks}
                disabled={checksRunning}
                className="bg-scoop-blue hover:bg-scoop-blue/90 shrink-0"
                data-testid="run-checks-btn"
              >
                {checksRunning
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</>
                  : <><Play className="w-4 h-4 mr-2" />Run All Checks</>}
              </Button>
            </div>

            <OverallScore results={checkResults} />

            {/* Checks grouped by category */}
            {groups.map(group => (
              <div key={group}>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 px-1">{group}</p>
                <div className="space-y-2">
                  {CHECKS.filter(c => c.group === group).map(check => {
                    const result = checkResults.find(r => r.id === check.id) || {};
                    const isExpanded = expandedCheck === check.id;
                    const hasDetail = result.detail || check.description;

                    return (
                      <div
                        key={check.id}
                        className={`rounded-lg border px-4 py-3 transition-all ${statusBg(result.pass)}`}
                      >
                        <div
                          className="flex items-center gap-3 cursor-pointer select-none"
                          onClick={() => hasDetail && setExpandedCheck(isExpanded ? null : check.id)}
                        >
                          <StatusIcon pass={result.pass} running={result.running} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-gray-800 truncate">{check.label}</span>
                              {result.value && (
                                <span className={`text-xs font-mono font-bold shrink-0 ${statusColor(result.pass)}`}>
                                  {result.value}
                                </span>
                              )}
                            </div>
                          </div>
                          {hasDetail && (
                            <button className="text-gray-400 hover:text-gray-600 shrink-0">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="mt-2 pl-7 space-y-1">
                            <p className="text-xs text-gray-500">{check.description}</p>
                            {result.detail && (
                              <p className={`text-xs font-medium ${statusColor(result.pass)}`}>
                                ↳ {result.detail}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Run history */}
            {runHistory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-gray-700">Run History</CardTitle>
                  <CardDescription>Last {MAX_HISTORY} check runs</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {runHistory.map((run, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs font-medium text-gray-700">{formatDateTime(run.timestamp)}</p>
                          <p className="text-xs text-gray-500">{run.passed}/{run.total} checks passed</p>
                        </div>
                        <div className={`text-lg font-black ${run.score === 100 ? "text-emerald-600" : run.score >= 70 ? "text-amber-500" : "text-red-500"}`}>
                          {run.score}%
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        )}

        {/* ── SUBSCRIBERS TAB ── */}
        {activeTab === "subscribers" && (
          <div className="space-y-5">
            {schedulerStatus?.digest_url_configured === false && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-bold mb-0.5">This environment cannot send digests</p>
                  <p>
                    No explicit <code className="bg-amber-100 px-1 rounded">SCOOP_FRONTEND_URL</code> is set.
                    Bulk email is opt-in per environment so a staging/preview clone can never email
                    subscribers with links to articles that only exist in its own database. Set the
                    variable on the production backend only.
                  </p>
                </div>
              </div>
            )}
            {schedulerStatus?.digest_enabled === false && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-bold mb-0.5">Daily digest is PAUSED</p>
                  <p>
                    <code className="bg-amber-100 px-1 rounded">DIGEST_ENABLED=false</code> is set in the
                    backend environment — no digest emails are being sent to subscribers. Remove the
                    variable (or set it to <code className="bg-amber-100 px-1 rounded">true</code>) and
                    restart the backend to resume. Test previews below still work while paused.
                  </p>
                </div>
              </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Email Digest Subscribers</h2>
                <p className="text-sm text-gray-500">Users who have opted in to the daily digest</p>
              </div>
              <Button onClick={fetchSubscribers} disabled={subscribersLoading} variant="outline" size="sm">
                {subscribersLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                  : <><RefreshCw className="w-4 h-4 mr-2" />Refresh</>}
              </Button>
            </div>

            {/* Count cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Mail className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{subscribers.length}</p>
                    <p className="text-xs text-gray-500">Active Subscribers</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg"><Users className="w-5 h-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {subscribers.length > 0
                        ? (() => {
                            const newest = subscribers[0]?.digest_subscribed_at || subscribers[0]?.created_at;
                            if (!newest) return "—";
                            const days = Math.floor((Date.now() - new Date(newest)) / 86400000);
                            return days === 0 ? "Today" : `${days}d ago`;
                          })()
                        : "—"}
                    </p>
                    <p className="text-xs text-gray-500">Most Recent Signup</p>
                  </div>
                </div>
              </CardContent></Card>
            </div>

            {/* Send a test digest (preview the email without touching subscribers) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-gray-700">Preview the Digest</CardTitle>
                <CardDescription>Send today's digest to one address to preview the email. Subscribers are not affected.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-scoop-blue/40"
                    data-testid="test-email-input"
                  />
                  <Button onClick={sendTestDigest} disabled={testSending} className="bg-scoop-blue hover:bg-scoop-blue/90" data-testid="send-test-btn">
                    {testSending
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                      : <><Mail className="w-4 h-4 mr-2" />Send Test</>}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Subscriber list */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-gray-700">Subscriber List</CardTitle>
                <CardDescription>Sorted by signup date, newest first</CardDescription>
              </CardHeader>
              <CardContent>
                {subscribersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : subscribers.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No subscribers yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {subscribers.map((sub, i) => (
                      <div key={i} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-blue-600">
                              {(sub.name || sub.email || "?")[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{sub.name || "—"}</p>
                            <p className="text-xs text-gray-500 truncate">{sub.email}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-4">
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">Subscribed</Badge>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {sub.digest_subscribed_at || sub.created_at
                              ? new Date(sub.digest_subscribed_at || sub.created_at).toLocaleDateString("en-US", {
                                  month: "short", day: "numeric", year: "numeric"
                                })
                              : "—"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </main>

      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-4xl mx-auto px-6 py-4 text-center text-xs text-gray-500">
          The Scoop Admin Panel · Auto-refreshing every 30 seconds
        </div>
      </footer>
    </div>
  );
};

export default AdminPage;
